import { useEffect } from 'react'
import { ChevronLeft, DeviceIconSvg, Gauge, WifiOff, X } from '../../icons'
import type { Device } from '../../types/device'
import { formatKW, formatMWh } from '../../lib/format'
import { PanelShell } from './PanelShell'

interface Props {
  device: Device
  onClose: () => void
  onBack: (() => void) | null
  showClose: boolean
  asSheet: boolean
}

function apparentKVA(m: Device['metrics']): string {
  if (!m?.apparent) return '—'
  return (m.apparent / 1000).toFixed(1)
}

export function MeteredDetails({ device, onClose, onBack, showClose, asSheet }: Props) {
  // Metered devices are read-only — they don't have a relay, so we never want
  // to fall into the muted 'off' state which would hide the live numbers on
  // dark surfaces (e.g. the Globale MWh hero box).
  const state = device.online ? 'on' : 'offline'
  const hasPhases = device.kind === 'meter-3ph' && !!device.phases?.length

  useEffect(() => {
    if (!showClose) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showClose])

  const m = device.metrics

  const body = (
    <div className="dpanel" data-state={state}>
      {onBack && (
        <button className="dpanel__back" type="button" onClick={onBack}>
          <ChevronLeft />
          <span>Panoramica sito</span>
        </button>
      )}
      <div className="dpanel__header">
        <div className="dpanel__title-wrap">
          <div className="dpanel__icon">
            <DeviceIconSvg name={device.icon} />
          </div>
          <div>
            <div className="dpanel__name">{device.name}</div>
            <div className="dpanel__id">{device.id}</div>
          </div>
        </div>
        {showClose && (
          <button className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        )}
      </div>

      <div className="dpanel__tag-row">
        <span className="dpanel__tag dpanel__tag--meter">
          <Gauge />
          <span>Energy meter</span>
        </span>
        <span className="dpanel__tag dpanel__tag--muted">Solo misurazione</span>
      </div>

      {!device.online && (
        <div className="meter-offline">
          <WifiOff />
          <div>
            <div className="meter-offline__title">Offline</div>
            <div className="meter-offline__sub">Dispositivo non raggiungibile — ultima lettura non disponibile.</div>
          </div>
        </div>
      )}

      {device.online && m && (
        <>
          <div className="dpanel__section-label">Totale</div>
          <div className="dpanel__metrics dpanel__metrics--3">
            <div className="metric metric--hero">
              <div className="metric__k">Potenza</div>
              <div className="metric__v"><span className="num">{formatKW(m.power)}</span> <span className="u">kW</span></div>
            </div>
            <div className="metric">
              <div className="metric__k">Potenza apparente</div>
              <div className="metric__v"><span className="num">{apparentKVA(m)}</span> <span className="u">kVA</span></div>
            </div>
            <div className="metric">
              <div className="metric__k">Corrente</div>
              <div className="metric__v">
                <span className="num">{m.current != null ? m.current.toFixed(1) : '—'}</span> <span className="u">A</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Tensione media</div>
              <div className="metric__v">
                <span className="num">{m.voltage != null ? m.voltage.toFixed(1) : '—'}</span> <span className="u">V</span>
              </div>
            </div>
            <div className="metric">
              <div className="metric__k">Fattore di pot.</div>
              <div className="metric__v"><span className="num">{m.pf != null ? m.pf.toFixed(2) : '—'}</span></div>
            </div>
            <div className="metric metric--energy">
              <div className="metric__k">Globale</div>
              <div className="metric__v"><span className="num">{formatMWh(m.energyWh)}</span> <span className="u">MWh</span></div>
            </div>
          </div>

          {hasPhases && device.phases && (
            <>
              <div className="dpanel__section-label">Per fase</div>
              <div className="phases phases--rich">
                {device.phases.map((p) => (
                  <div key={p.label} className="phase">
                    <div className="phase__label">Fase {p.label}</div>
                    <div className="phase__grid phase__grid--rich">
                      <div><span className="phase__k">P</span><span className="phase__v">{formatKW(p.power)}<span className="u"> kW</span></span></div>
                      <div><span className="phase__k">S</span><span className="phase__v">{(p.apparent / 1000).toFixed(1)}<span className="u"> kVA</span></span></div>
                      <div><span className="phase__k">V</span><span className="phase__v">{p.voltage.toFixed(1)}<span className="u"> V</span></span></div>
                      <div><span className="phase__k">I</span><span className="phase__v">{p.current.toFixed(1)}<span className="u"> A</span></span></div>
                      <div><span className="phase__k">PF</span><span className="phase__v">{p.pf.toFixed(2)}</span></div>
                      <div><span className="phase__k">E</span><span className="phase__v">{formatMWh(p.energyWh)}<span className="u"> MWh</span></span></div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="dpanel__footer">
        <div className="dpanel__footer-k">Modello</div>
        <div className="dpanel__footer-v">{device.model || `Shelly Gen ${device.gen ?? '?'}`}</div>
        <div className="dpanel__footer-k">Ultima lettura</div>
        <div className="dpanel__footer-v">pochi secondi fa</div>
      </div>
    </div>
  )

  return <PanelShell asSheet={asSheet} onClose={onClose}>{body}</PanelShell>
}
